// src/lib/anexos.js — Anexos múltiplos tipados (Feature 018): Despesa / Conta a Pagar / Conta a Receber.
// Usa a tabela `comprovantes` (tipo + FKs despesa_id / conta_pagar_id / conta_receber_id + empresa_id).
// Upload no bucket privado financeiro-docs; exibição por URL assinada (signFotosUrls/abrirArquivoSeguro).
//
// Uso no painel 360°:
//   import { anexosBoxPlaceholder, refreshAnexosSec } from '../../lib/anexos.js';
//   ... dentro do HTML do sheet: ${anexosBoxPlaceholder()}
//   ... depois de setar innerHTML: refreshAnexosSec('cp', p.id);   // 'cp' | 'cr' | 'despesa' | 'fc'

import { sb } from '../api/supabaseClient.js';
import { tenant } from '../context/tenantContext.js';
import { showToast } from './toast.js';
import { esc } from './dom.js';
import { uiIco } from './uiIcons.js';
import { signFotosUrls, abrirArquivoSeguro } from './signedUrl.js';
import { comprimirImagem } from './imageCompress.js';

const FK = { despesa: 'despesa_id', cp: 'conta_pagar_id', cr: 'conta_receber_id', fc: 'fluxo_caixa_id' };
const TIPO_LBL = { comprovante: 'Comprovante', nota_fiscal: 'Nota fiscal', outro: 'Outro' };

// Placeholder síncrono para o HTML do sheet; refreshAnexosSec preenche depois (assíncrono).
export function anexosBoxPlaceholder() {
  return '<div class="sc-sheet-sec" id="anexos-box"><h4>Anexos</h4><div style="font:400 13px/18px inherit;color:var(--muted-foreground)">Carregando…</div></div>';
}

export async function fetchAnexos(entidade, id) {
  const col = FK[entidade];
  if (!col || !id) return [];
  const { data } = await sb.from('comprovantes').select('*').eq(col, id).order('created_at', { ascending: false });
  const arr = data || [];
  try { await signFotosUrls(arr, 'url'); } catch (_) { /* mantém original */ }
  return arr;
}

function _sec(anexos, entidade, id) {
  const items = (anexos || []).map((a) => `
    <li style="display:flex;align-items:center;gap:8px;padding:8px 10px;border:1px solid var(--border);border-radius:var(--radius-md);margin-bottom:6px">
      <span class="sc-badge outline" style="flex-shrink:0">${esc(TIPO_LBL[a.tipo] || a.tipo || 'Anexo')}</span>
      <span style="flex:1;min-width:0;font:500 13px/18px inherit;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(a.nome_arquivo || 'arquivo')}</span>
      <button class="sc-btn outline icon sm" title="Abrir" onclick="abrirArquivoSeguro('${(a.url || '').replace(/'/g, '%27')}')">${uiIco('link', 13)}</button>
      <button class="sc-btn outline icon sm" title="Remover" style="color:var(--destructive)" onclick="_anexoRemover('${a.id}','${entidade}','${id}')">${uiIco('trash', 13)}</button>
    </li>`).join('');
  return `<div class="sc-sheet-sec" id="anexos-box">
    <h4>Anexos${anexos && anexos.length ? ` <span class="ct">${anexos.length}</span>` : ''}</h4>
    <ul style="list-style:none;padding:0;margin:0 0 8px">${items || '<li style="font:italic 400 13px/18px inherit;color:var(--muted-foreground)">Nenhum anexo ainda.</li>'}</ul>
    <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center">
      <select class="sc-input" id="anexo-tipo" style="width:auto;height:32px">
        <option value="comprovante">Comprovante de pagamento</option>
        <option value="nota_fiscal">Nota fiscal</option>
        <option value="outro">Outro</option>
      </select>
      <label class="sc-btn outline sm" style="cursor:pointer">${uiIco('clip', 13)} + Anexo
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.xml" style="display:none" onchange="_anexoUpload(this,'${entidade}','${id}')"></label>
    </div>
  </div>`;
}

export async function refreshAnexosSec(entidade, id) {
  const box = document.getElementById('anexos-box');
  if (!box) return;
  const anexos = await fetchAnexos(entidade, id);
  box.outerHTML = _sec(anexos, entidade, id);
}

async function _anexoUpload(input, entidade, id) {
  const file = input && input.files && input.files[0];
  if (!file) return;
  if (file.size > 20 * 1024 * 1024) { showToast('Arquivo muito grande. Máx 20MB.', 'error'); input.value = ''; return; }
  const tipoEl = document.getElementById('anexo-tipo');
  const tipo = tipoEl ? tipoEl.value : 'comprovante';
  showToast('Enviando anexo…');
  try {
    let up = file;
    if (/^image\//.test(file.type || '')) { try { up = await comprimirImagem(file); } catch (_) { up = file; } }
    const safe = (up.name || file.name).replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${entidade}/${id}/${Date.now()}_${safe}`;
    const { error: upErr } = await sb.storage.from('financeiro-docs').upload(path, up, { upsert: true });
    if (upErr) throw upErr;
    const { data: u } = sb.storage.from('financeiro-docs').getPublicUrl(path);
    const row = { url: u.publicUrl, nome_arquivo: file.name, tipo, empresa_id: tenant.empresaId };
    row[FK[entidade]] = id;
    const { error: dbErr } = await sb.from('comprovantes').insert(row);
    if (dbErr) throw dbErr;
    showToast('Anexo adicionado!', 'success');
    await refreshAnexosSec(entidade, id);
  } catch (err) {
    showToast('Erro ao anexar: ' + (err.message || err), 'error');
  }
}

async function _anexoRemover(anexoId, entidade, id) {
  if (!window.confirm('Remover este anexo?')) return;
  const { error } = await sb.from('comprovantes').delete().eq('id', anexoId);
  if (error) { showToast('Erro ao remover: ' + error.message, 'error'); return; }
  showToast('Anexo removido.');
  await refreshAnexosSec(entidade, id);
}

if (typeof window !== 'undefined') {
  window._anexoUpload = _anexoUpload;
  window._anexoRemover = _anexoRemover;
  if (!window.abrirArquivoSeguro) window.abrirArquivoSeguro = abrirArquivoSeguro;
}
